import React from 'react';
import ReactDom from 'react-dom';
import axios from 'helpers/axios';
import useInView from 'react-cool-inview';

import { Container, Label, ModalPosition, Spinner } from 'ui/components/styled';
import { ConfirmationModal } from 'ui/components/styled/NewModal/ConfirmationModal';
import { useCompany, useToasts, useMutableRef, useOpenState, useLayout, useWebSocket } from 'hooks';
import { Loader, Row, ActionButton } from './styled';
import { ConfirmUnarchive } from '../modals';

export const ArchivedCustomers = ({ onRefresh, isSmall }) => {
  const ref = React.useRef();
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [perPage] = React.useState(10);
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const [modal, openModal, closeModal] = useOpenState();
  const { errorToast, removeAllToasts } = useToasts();
  const { subscribe, unsubscribe } = useWebSocket();
  const { activeCompany } = useCompany();
  const { isLoading: isBusy, setLayoutLoading } = useLayout();
  const modalRoot = document.getElementById('dashboard-container') || document.body;

  const fetchData = async (page) => {
    setLoading(true);

    try {
      const top = page * perPage;
      const skip = 0;
      const response = await axios.get(
        `/customers/?$top=${top}&$skip=${skip}&$orderby=name&$order=asc&$cid=${activeCompany}&$archived=true`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotal(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Archived Customers.' });
    }

    setLoading(false);
  };

  const removeCustomer = async (customer) => {
    try {
      const response = await axios.delete(`/customers/${customer.id}`);

      if (response.status === 204) {
        removeAllToasts();
        refresh();
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Remove Customer.' });
    }

    closeModal();
  };

  const unarchiveCustomer = async (customer, matchOrphans = false) => {
    try {
      if (matchOrphans) setLayoutLoading(true);
      const response = await axios.post(
        `/customers/${customer.id}/unarchive?$matchOrphans=${matchOrphans}`
      );

      if (response.status === 200) {
        removeAllToasts();
        //refresh(true); // will be performed by realtime updates handler
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Unarchive Customer.' });
    }

    closeModal();
    setLayoutLoading(false);
  };

  const refresh = (refreshDashboard = false) => {
    fetchData(1);
    if (refreshDashboard && onRefresh) {
      onRefresh();
    }
  };

  React.useEffect(() => {
    fetchData(1);
  }, []);

  const { observe } = useInView({
    root: ref.current,
    onEnter: ({ unobserve }) => {
      if (loading) return;

      const hasMore = data.length < total;
      if (!hasMore) {
        unobserve();
        return;
      }

      // Load more data
      const nextPage = currentPage.current + 1;
      setCurrentPage(nextPage);
      fetchData(nextPage);
    },
  });

  const handleRealtimeUpdates = () => {
    refresh(true);
  };

  React.useEffect(() => {
    if (!data?.length) return;
    subscribe('customer:unarchive', handleRealtimeUpdates);

    return () => unsubscribe('customer:unarchive', handleRealtimeUpdates);
  }, [data]);

  return (
    <>
      <Container>
        <Label color="darkGrey" fontSize="15px">
          Customers
        </Label>
      </Container>
      <Container ref={ref} maxHeight="175px" overflow="auto" mb="l" fontSize="10px">
        {data.map((customer, index) => (
          <Row ref={index === data.length - 1 ? observe : null} key={index}>
            <Label fontWeight={400} fontSize={isSmall ? '12px' : '14px'}>
              {customer.name}
            </Label>
            <Container
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-end"
              width="auto"
            >
              {customer.lockedAt && <Spinner size="20px" />}
              <ActionButton
                variant="danger-dark"
                onClick={() => openModal('confirm-remove', { customer })}
                disabled={loading || customer.lockedAt}
                mx="xs"
              >
                Delete
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={() => openModal('confirm-unarchive', { customer })}
                disabled={loading || customer.lockedAt}
              >
                Unarchive
              </ActionButton>
            </Container>
          </Row>
        ))}
        {loading && <Loader />}
      </Container>
      {ReactDom.createPortal(
        <>
          <ConfirmationModal
            show={modal.isOpen('confirm-remove')}
            position={ModalPosition.TOP_CENTER}
            top="30%"
            width="35%"
            maxWidth="550px"
            onClose={closeModal}
            onConfirm={() => removeCustomer(modal.params?.customer)}
            cancelButtonProps={{ variant: 'contrast-primary', boxShadow: 'none' }}
            confirmButtonProps={{ variant: 'danger', boxShadow: 'none', text: 'Yes Delete' }}
            stopClickPropagation
          >
            Are you sure you want to permanently delete customer &quot;
            {modal.params?.customer?.name}&quot; ? All the monitors associated with this customer
            would also be removed.
          </ConfirmationModal>
          <ConfirmUnarchive
            show={modal.isOpen('confirm-unarchive')}
            position={ModalPosition.TOP_CENTER}
            top="25%"
            width="35%"
            maxWidth="550px"
            onClose={closeModal}
            onConfirm={({ checked }) => unarchiveCustomer(modal.params?.customer, checked)}
            title={`Unarchive customer "${modal.params?.customer?.name}"`}
            checkboxProps={{
              label: 'Would you like to match orphans with this customer monitors?',
            }}
            closeOnOutsideClick={!isBusy}
            stopClickPropagation
          />
        </>,
        modalRoot
      )}
    </>
  );
};
